import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Switch, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip,
} from '@mui/material';
import {
  ArrowBackRounded, CheckRounded, AddRounded, PublicRounded, TouchAppRounded, LockRounded,
  MarkEmailReadRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useQueues } from '../../hooks/useQueues';
import { useProviders, ProviderOption } from '../../hooks/useProviders';
import {
  DayOfWeek, DayScheduleInput, QueueBookingMode, BOOKING_MODES, DAYS,
} from '../../api/queueTypes';

interface Row extends DayScheduleInput { start_time: string; end_time: string; max_tokens: number; time_per_token_minutes: number; }

const defaultRows = (): Row[] =>
  DAYS.map((d) => ({ day: d, is_enabled: d !== 'sunday', start_time: '09:00', end_time: '18:00', max_tokens: 40, time_per_token_minutes: 10 }));

const MODE_ICON: Record<QueueBookingMode, React.ReactElement> = {
  public: <PublicRounded />, controlled_public: <TouchAppRounded />, private: <LockRounded />,
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px', p: 2.5 };

const QueueFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { getQueue, createQueue, updateQueue, previewScheduleChange } = useQueues();
  const { getProviders } = useProviders();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [bookingMode, setBookingMode] = useState<QueueBookingMode>('public');
  const [rows, setRows] = useState<Row[]>(defaultRows());
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [submittedModal, setSubmittedModal] = useState(false);
  const [affectDialog, setAffectDialog] = useState<{ count: number } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const pool = await getProviders();
        setProviders(pool);
      } catch { /* providers optional */ }
      if (isEdit && id) {
        try {
          const q = await getQueue(id);
          setName(q.name);
          setBookingMode(q.booking_mode);
          setProviderIds(q.providers.map((p) => p.user_id));
          const byDay: Record<string, Row> = {};
          q.schedule.forEach((s) => {
            byDay[s.day] = {
              day: s.day, is_enabled: s.is_enabled,
              start_time: s.start_time || '09:00', end_time: s.end_time || '18:00',
              max_tokens: s.max_tokens ?? 40, time_per_token_minutes: s.time_per_token_minutes ?? 10,
            };
          });
          setRows(DAYS.map((d) => byDay[d] || { day: d as DayOfWeek, is_enabled: false, start_time: '09:00', end_time: '18:00', max_tokens: 40, time_per_token_minutes: 10 }));
        } catch (e: any) {
          setError(e?.response?.data?.message || 'Failed to load queue.');
        }
      }
      setLoading(false);
    })();
  }, [id, isEdit, getQueue, getProviders]);

  const toggleProvider = (uid: string) =>
    setProviderIds((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));

  const updateRow = (idx: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const applyMondayToAll = () => {
    const m = rows[0];
    setRows((prev) => prev.map((r) => ({ ...r, is_enabled: true, start_time: m.start_time, end_time: m.end_time, max_tokens: m.max_tokens, time_per_token_minutes: m.time_per_token_minutes })));
    setToast('Applied Monday schedule to all days');
  };

  const buildSchedule = (): DayScheduleInput[] =>
    rows.map((r) => (r.is_enabled
      ? { day: r.day, is_enabled: true, start_time: r.start_time, end_time: r.end_time, max_tokens: Number(r.max_tokens), time_per_token_minutes: Number(r.time_per_token_minutes) }
      : { day: r.day, is_enabled: false }));

  const validate = (): string | null => {
    if (!name.trim()) return 'Enter a queue name.';
    if (providerIds.length === 0) return 'Select at least one provider.';
    if (!rows.some((r) => r.is_enabled)) return 'Enable at least one day in the schedule.';
    return null;
  };

  const doCreate = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      await createQueue({ name: name.trim(), provider_ids: providerIds, schedule: buildSchedule(), booking_mode: bookingMode });
      setSubmittedModal(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create queue.');
    } finally { setSaving(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, providerIds, bookingMode, rows, createQueue]);

  const doUpdate = useCallback(async (cancelExceeding: boolean) => {
    if (!id) return;
    setSaving(true); setError(null);
    try {
      await updateQueue(id, {
        name: name.trim(), provider_ids: providerIds, schedule: buildSchedule(), booking_mode: bookingMode,
        update_existing_slots: true, cancel_exceeding_tokens: cancelExceeding,
      });
      setToast('Queue updated');
      setTimeout(() => navigate(`/queues/${id}`), 600);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update queue.');
    } finally { setSaving(false); setAffectDialog(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name, providerIds, bookingMode, rows, updateQueue, navigate]);

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    if (!isEdit) return doCreate();
    // edit: preview schedule impact first
    try {
      setSaving(true);
      const preview = await previewScheduleChange(id!, { schedule: buildSchedule() });
      setSaving(false);
      if (preview.has_affected_tokens && preview.total_tokens_to_cancel > 0) {
        setAffectDialog({ count: preview.total_tokens_to_cancel });
      } else {
        doUpdate(false);
      }
    } catch {
      setSaving(false);
      doUpdate(false); // preview is best-effort
    }
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Button startIcon={<ArrowBackRounded />} variant="text" onClick={() => navigate(isEdit ? `/queues/${id}` : '/queues')}>Back</Button>

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {/* Details */}
      <Box sx={{ ...card, mt: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Queue details</Typography>
        <TextField fullWidth label="Queue name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. General OPD" sx={{ mb: 1 }} />
      </Box>

      {/* Providers */}
      <Box sx={{ ...card, mt: 2.25 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>Providers</Typography>
        {providers.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontSize: 14 }}>No providers found. Add team members (with provider access) first.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {providers.map((p) => {
              const sel = providerIds.includes(p.user_id);
              return (
                <Chip
                  key={p.user_id}
                  label={p.name}
                  icon={sel ? <CheckRounded /> : <AddRounded />}
                  onClick={() => toggleProvider(p.user_id)}
                  variant={sel ? 'filled' : 'outlined'}
                  color={sel ? 'primary' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* Booking mode */}
      <Box sx={{ ...card, mt: 2.25 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>Booking mode</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {BOOKING_MODES.map((m) => {
            const sel = bookingMode === m.id;
            return (
              <Box key={m.id} onClick={() => setBookingMode(m.id)} sx={{
                display: 'flex', gap: 1.5, alignItems: 'center', p: 1.75, borderRadius: '12px', cursor: 'pointer',
                border: `1.5px solid ${sel ? colors.primary : colors.inputBorder}`, bgcolor: sel ? colors.primaryLight : 'transparent',
              }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: colors.backgroundPaper, display: 'grid', placeItems: 'center', color: colors.primary }}>{MODE_ICON[m.id]}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>{m.desc}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Schedule */}
      <Box sx={{ ...card, mt: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5">Weekly schedule</Typography>
          <Button size="small" variant="text" onClick={applyMondayToAll}>Apply Monday to all</Button>
        </Box>
        {rows.map((r, idx) => (
          <Box key={r.day} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: `1px solid ${colors.border}`, opacity: r.is_enabled ? 1 : 0.55, flexWrap: 'wrap' }}>
            <Switch checked={r.is_enabled} onChange={(e) => updateRow(idx, { is_enabled: e.target.checked })} />
            <Typography sx={{ width: 42, fontWeight: 700, fontSize: 13 }}>{cap(r.day).slice(0, 3)}</Typography>
            <TextField type="time" size="small" value={r.start_time} disabled={!r.is_enabled} onChange={(e) => updateRow(idx, { start_time: e.target.value })} sx={{ width: 120 }} />
            <Typography sx={{ color: colors.textSecondary }}>to</Typography>
            <TextField type="time" size="small" value={r.end_time} disabled={!r.is_enabled} onChange={(e) => updateRow(idx, { end_time: e.target.value })} sx={{ width: 120 }} />
            <TextField type="number" size="small" label="Max" value={r.max_tokens} disabled={!r.is_enabled} onChange={(e) => updateRow(idx, { max_tokens: +e.target.value })} sx={{ width: 90 }} />
            <TextField type="number" size="small" label="Min/token" value={r.time_per_token_minutes} disabled={!r.is_enabled} onChange={(e) => updateRow(idx, { time_per_token_minutes: +e.target.value })} sx={{ width: 110 }} />
          </Box>
        ))}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(isEdit ? `/queues/${id}` : '/queues')}>Cancel</Button>
        <Button variant="contained" startIcon={<CheckRounded />} disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={22} color="inherit" /> : isEdit ? 'Save changes' : 'Create queue'}
        </Button>
      </Box>

      {/* Edit: affected-tokens confirm */}
      <Dialog open={!!affectDialog} onClose={() => setAffectDialog(null)}>
        <DialogTitle>Reduce capacity?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This change will cancel <b>{affectDialog?.count}</b> already-booked token(s) on affected slots. Affected customers should be notified. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAffectDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => doUpdate(true)}>Proceed anyway</Button>
        </DialogActions>
      </Dialog>

      {/* Create: submitted-for-activation */}
      <Dialog open={submittedModal} onClose={() => { setSubmittedModal(false); navigate('/queues'); }}>
        <DialogTitle sx={{ textAlign: 'center' }}>Queue submitted</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '20px', bgcolor: '#FFFBEB', color: '#D97706', display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.5 }}>
            <MarkEmailReadRounded sx={{ fontSize: 34 }} />
          </Box>
          <DialogContentText>
            Queue submitted for activation. We'll email a payment link — complete payment to activate it automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5 }}>
          <Button variant="contained" onClick={() => { setSubmittedModal(false); navigate('/queues'); }}>Got it</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default QueueFormPage;
